import { useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react'
import { Box, Typography } from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { FS_XS, FS_SM } from '../common/theme'

export interface TaDropZoneProps {
  // Receives the dropped files (from a drop event or from the click-to-browse
  // picker). Caller is responsible for filtering / staging.
  onFiles: (files: FileList) => void
  // Forwarded to the underlying <input type="file">.
  accept?: string
  multiple?: boolean

  // Visible band - caller can override the icon / label / sub-label and the
  // band sx (border thickness, padding, etc.). When children are provided
  // the outer wrapper picks up `wrapperSx` instead.
  icon?: ReactNode
  label?: string
  subLabel?: string
  sx?: any
  wrapperSx?: any

  // When children are provided, the drag handlers attach to the outer
  // wrapper so drops anywhere over the wrapper (not just the dashed band)
  // are accepted. The dashed band still renders at the top as the visible
  // affordance. Without children the dashed band itself is the drop area.
  children?: ReactNode

  // Hide the dashed band visual while keeping the drop wiring active -
  // useful when the caller has already staged a file and wants to suppress
  // the affordance.
  showZone?: boolean

  // Imperative handle - pass a ref here to call `openPicker()` from the
  // parent. Renamed from React's special `ref` prop so the component can
  // be a plain function declaration (no `forwardRef` wrapper required).
  handleRef?: Ref<TaDropZoneHandle>

  testId?: string
  inputTestId?: string
}

// Imperative handle - exposes `openPicker()` so callers that hide the dashed
// band but still want a secondary "Add more files" trigger can open the same
// hidden file input without mounting a second one.
export interface TaDropZoneHandle {
  openPicker: () => void
}

// Shared drag-and-drop file zone - dashed border, hover/drag highlight, a
// hidden <input type="file"> for click-to-browse, and unified file delivery
// via `onFiles`. Replaces three near-identical inline implementations across
// KbFolderPanel, KbAddFilesModal, and BidImportQuestionsModal.
export function TaDropZone({
  onFiles, accept, multiple = false,
  icon, label = 'Drop files here or click to browse', subLabel,
  sx, wrapperSx, children, showZone = true, testId, inputTestId,
  handleRef,
}: TaDropZoneProps) {

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  useImperativeHandle(handleRef, () => ({ openPicker: () => fileInputRef.current?.click() }), [])

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true) }
  const onDragLeave = () => setIsDragging(false)
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files)
  }
  const openPicker = () => fileInputRef.current?.click()

  const band = showZone ? (
    <Box
      data-testid={testId}
      onClick={openPicker}
      onDragOver={children ? undefined : onDragOver}
      onDragLeave={children ? undefined : onDragLeave}
      onDrop={children ? undefined : onDrop}
      sx={{
        border: 2, borderStyle: 'dashed',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 1, py: 2, px: 2,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, justifyContent: 'center',
        cursor: 'pointer',
        bgcolor: isDragging ? 'action.selected' : 'action.hover',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.selected' },
        ...sx,
      }}
    >
      {icon ?? <CloudUploadOutlinedIcon sx={{ fontSize: 28, opacity: 0.5 }} />}
      <Typography variant="caption" sx={{ opacity: 0.65, fontSize: FS_SM, textAlign: 'center' }}>
        {label}
      </Typography>
      {subLabel && (
        <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>
          {subLabel}
        </Typography>
      )}
    </Box>
  ) : null

  const input = (
    <input
      ref={fileInputRef}
      data-testid={inputTestId}
      type="file"
      accept={accept}
      multiple={multiple}
      style={{ display: 'none' }}
      onChange={e => {
        if (e.target.files?.length) onFiles(e.target.files)
        e.target.value = ''
      }}
    />
  )

  // Wrapper mode - outer Box absorbs drops anywhere over the children area.
  if (children) {
    return (
      <Box
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...wrapperSx }}
      >
        {band}
        {children}
        {input}
      </Box>
    )
  }

  // Standalone - the dashed band itself is the drop area.
  return (
    <>
      {band}
      {input}
    </>
  )
}


